/* eslint-disable no-empty */

import * as UploadService from "@/services/upload/upload.service";
import { startBlinkingTitle } from "@/utils/blinkTitle";
import * as NotificationService from "@/services/notification.service";


export const upload = {
    namespaced: true,   
    state: {
        progresses: [],  // 업로드 진행중
        pendingFiles: [],  // 업로드 대기
        failedFiles: [],  // 실패 목록
        // Drag 상태
        isDragged: false,
        isDropped: false,

        overwrite: false
    },
    getters: {
        getProgressByName:
            (state) =>
                (name) =>
                    state.progresses.find(p => p.name === name),
        getUploadedBytesByName:
            (state) =>
                (name) =>
                    state.progresses.find(p => p.name === name)?.uploadedBytes ?? 0,
    },
    actions: {
        setDragged({ commit }, value) {
            commit("SET_DRAGGED", value);
        },
        setDropped({ commit }, value) {
            commit("SET_DROPPED", value);
        },
        async enqueueFiles({ commit }, files) {
            const pendingFiles = await UploadService.collectFiles(files);
            for (const file of pendingFiles) {
                commit("ADD_PENDING_FILE", file);
            }
        },
        async enqueueDropFiles({ commit }, items) {
            const pendingFiles = await UploadService.collectDropFiles(items);
            for (const file of pendingFiles) {
                commit("ADD_PENDING_FILE", file);
            }
        },
        removePendingFile({ commit }, index) {
            commit("REMOVE_PENDING_FILE", index);
        },
        clearPendingFiles({ commit }) {
            commit("CLEAR_PENDING_FILES");
        },
        async startUpload({
                state,
                dispatch,
                commit,
                rootGetters
            }, { overwrite })
        {
            const uploadFiles = state.pendingFiles.filter(file => {
                const name = file.entry.name;

                return !state.progresses.some(
                    item =>
                        item.path === file.path &&
                        item.name === name
                );
            });

            for (const file of uploadFiles) {
                dispatch("registerProgress", {
                    path: file.path,
                    name: file.entry.name,
                    totalFileSize: 0,
                });
            }

            commit("CLEAR_PENDING_FILES");
            commit("SET_OVERWRITE", false);

            await UploadService.startUpload({
                uploadFiles,
                curDir:rootGetters["files/currentDirPath"],
                overwrite,
                async onStart(file) {
                    dispatch("startProgress", file);
                },
                async onProgress(progress) {
                    await dispatch("updateProgress", progress);
                },
                async onComplete(result) {
                    await dispatch("finishUpload", result);
                },
                async onFail(result) {
                    await dispatch("failUpload", result);
                },
                onFinish(summary) {
                    dispatch("clearPendingFiles");
                    for (const file of uploadFiles) {
                        commit("REMOVE_PROGRESS", {
                            path: file.path,
                            name: file.entry.name,
                        });
                    }
                    if (summary.success) {
                        startBlinkingTitle();
                    }
                }
            });
        },
        async retryUpload({ state, dispatch, })
        {
            await UploadService.retryUpload({failedFiles:state.failedFiles, dispatch,});
        },
        cancelUpload({ commit, }) {
            commit("CLEAR_PENDING_FILES");
            commit("SET_DROPPED", false);
            commit("SET_OVERWRITE", false);
        },
        updateProgress({ commit }, payload) {
            commit("UPDATE_PROGRESS", payload);
        },
        async finishUpload({ dispatch }, result) {
            await dispatch("markProgressSuccess", {
                path: result.targetPath,
                name: result.file.name,
            });

            await dispatch("notifyUpload", result);

            if (result.type === "success") {
                await dispatch("updateUploadedFile", result);
            }

            setTimeout(() => {
                dispatch("removeProgress", {
                    path: result.targetPath,
                    name: result.file.name,
                });
            }, 1500);
        },
        notifyUpload(context, result) {
            switch (result.type) {
                case "success":
                    NotificationService.success(result.file.name);
                    break;
                case "exists":
                    NotificationService.warning(`file exist\n${result.file.name}`);
                    break;
                case "failed":
                    NotificationService.error(`${result.file.name} 업로드 실패`);
                    break;
            }
        },
        updateUploadedFile({ dispatch }, result) {
            const {
                response,
                curDir,
                targetPath
            } = result;
            switch (response.data.status) {
                case "same location":
                    if (targetPath === "") {
                        dispatch("files/setFile", {
                                path: curDir,
                                name: response.data.fileInfo.file.name,
                                meta: response.data.fileInfo.meta,
                                isFile: true,
                        }, { root: true });
                    } else {
                        dispatch("files/setFile",{
                                path: curDir,
                                name: targetPath.split("/")[0],
                                meta: { size: 0 },
                                isFile: false,
                        }, { root: true });
                    }
                    break;
                case "other location":
                    break;
                case "stat error":
                    console.error(response.data.error);
                    break;
            }
        },
        async failUpload({commit, dispatch}, payload) {
            commit("ADD_FAILED_FILE", payload);
            dispatch("removeProgress", { path: payload.file.path, name: payload.file.name });
            dispatch("notifyUpload",
                {
                    type: "failed",
                    file: payload.file
                }
            );
        },
        clearFailedFiles({ commit, }) {
            commit("CLEAR_FAILED_FILES");
        },
        registerProgress({ state, commit }, payload) {
            const exists = state.progresses.some(
                item =>
                    item.path === payload.path &&
                    item.name === payload.name
            );

            if (exists) {
                return;
            }

            commit("REGISTER_PROGRESS", payload);
        },
        removeProgress({ commit }, { path, name }) {
            commit("REMOVE_PROGRESS", { path, name });
        },
        clearProgress({ commit }) {
            commit("CLEAR_PROGRESS");
        },
        setOverwrite({ commit }, value ) {
            commit("SET_OVERWRITE", value);
        },
        startProgress({ commit }, file) {
            commit("START_PROGRESS", file);
        },
        markProgressSuccess({ commit }, name) {
            commit("MARK_PROGRESS_SUCCESS", name);
        },
    },
    mutations: {
        SET_DRAGGED(state, value) {
            state.isDragged = value;
        },
        SET_DROPPED(state, value) {
            state.isDropped = value;
        },
        ADD_PENDING_FILE(state, file) {
            state.pendingFiles.push(file);
        },
        REMOVE_PENDING_FILE(state, index) {
            state.pendingFiles.splice(index, 1);
        },
        CLEAR_PENDING_FILES(state) {
            state.pendingFiles.length = 0;
        },
        ADD_FAILED_FILE(state, file) {
            state.failedFiles.push(file);
        },
        REMOVE_FAILED_FILE(state, name) {
            state.failedFiles = state.failedFiles.filter(
                    item => item.name !== name
                );
        },
        CLEAR_FAILED_FILES(state) {
            state.failedFiles.length = 0;
        },
        UPDATE_PROGRESS(state, payload) {
            const {
                name,
                chunkSize = 0,
                totalFileSize = null
            } = payload;
            const now = Date.now();
            const progress = state.progresses.find(
                p => p.name === name
            );
            if (!progress) {
                state.progresses.push({
                    name,
                    uploadedBytes: chunkSize,
                    totalFileSize,
                    uploadPercent: totalFileSize
                        ? Math.floor(
                            chunkSize /
                            totalFileSize *
                            100
                        )
                        : 0,
                    remainingBytes: totalFileSize
                        ? totalFileSize - chunkSize
                        : 0,
                    uploadRatePerSecond: null,
                    previousUploadStats: {
                        uploadedBytes: chunkSize,
                        timestamp: now
                    }
                });
                return;
            }
            progress.uploadedBytes += chunkSize;
            progress.uploadPercent = Math.min(Math.floor(progress.uploadedBytes / progress.totalFileSize * 100), 100);
            progress.remainingBytes = Math.max(progress.totalFileSize - progress.uploadedBytes, 0);
            if (!progress.previousUploadStats) {
                progress.previousUploadStats = {
                    uploadedBytes: progress.uploadedBytes,
                    timestamp: now
                };
            }
            else {
                const deltaTime = (now - progress.previousUploadStats.timestamp) / 1000;
                if (deltaTime >= 3) {
                    const deltaBytes = progress.uploadedBytes - progress.previousUploadStats.uploadedBytes;
                    progress.uploadRatePerSecond = Math.floor(deltaBytes / deltaTime);
                    progress.previousUploadStats = {
                        uploadedBytes: progress.uploadedBytes,
                        timestamp: now
                    };
                }
            }
        },
        REMOVE_PROGRESS(state, { path, name }) {
            state.progresses = state.progresses.filter(
                item =>
                    item.path !== path ||
                    item.name !== name
            );
        },
        CLEAR_PROGRESS(state) {
            state.progresses.length = 0;
        },
        async finishUpload({ dispatch }, result) {
            dispatch("removeProgress", result.file.name);
            dispatch("notifyUpload", result);
            if (result.type === "success") {
                dispatch("updateUploadedFile", result);
            }
        },
        REGISTER_PROGRESS(state, payload) {
            state.progresses.push({
                path: payload.path,
                name: payload.name,
                uploadedBytes: 0,
                totalFileSize: payload.totalFileSize,
                uploadPercent: 0,
                remainingBytes: payload.totalFileSize,
                uploadRatePerSecond: 0,
                status: "waiting",
                previousUploadStats:
                {
                    uploadedBytes: 0,
                    timestamp: Date.now()
                }
            });
        },
        SET_OVERWRITE(state, value) {
            state.overwrite = value;
        },
        START_PROGRESS(state, file) {
            const progress = state.progresses.find(p => p.name === file.name && p.path === file.path);
            if (!progress)
                return;

            progress.status = "uploading";
            progress.totalFileSize = file.size;
            progress.remainingBytes = file.size;
        },
        MARK_PROGRESS_SUCCESS(state, { path, name }) {
            const progress = state.progresses.find(p => p.name === name && p.path === path);
            if (!progress)
                return;

            progress.uploadPercent = 100;
            progress.remainingBytes = 0;
            progress.status = "success";
        }
    }
};